/**
 * IdeasInput.js
 * Topic input + mode selector + platform selector + submit button.
 * Pure presentation — all state owned by GenerateIdeas orchestrator.
 */
import React from 'react';
import {
    Box, Paper, Typography, TextField, Button,
    Avatar, CircularProgress, alpha, Tooltip, useTheme,
} from '@mui/material';
import {
    AutoAwesome as AutoAwesomeIcon,
    Psychology as PsychologyIcon,
    Lock as LockIcon,
} from '@mui/icons-material';
import { PLATFORMS, EXAMPLE_TOPICS, PLATFORM_COLORS, IDEA_MODES } from './constants';

const MODE_COLORS = {
    smart:        '#6366f1',
    fill_gap:     '#10b981',
    double_down:  '#f59e0b',
    trending_you: '#ec4899',
};

const MODE_EMOJI = {
    smart:        '✨',
    fill_gap:     '🔍',
    double_down:  '⚡',
    trending_you: '📈',
};

export default function IdeasInput({
    topic, onTopicChange,
    platform, onPlatformChange,
    mode, onModeChange,
    isPro,
    onSubmit, loading, isValid,
    card,
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const selectedMode = IDEA_MODES.find(m => m.value === mode) || IDEA_MODES[0];
    const hideTopicField = isPro && (mode === 'double_down' || mode === 'trending_you');

    return (
        <Paper elevation={0} sx={{ ...card, mb: 0 }}>
            {/* Section header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Avatar sx={{ bgcolor: alpha('#6366f1', 0.12), color: '#6366f1', width: 38, height: 38 }}>
                    <PsychologyIcon sx={{ fontSize: 20 }} />
                </Avatar>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        Generation Mode
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {selectedMode.description}
                    </Typography>
                </Box>
            </Box>

            {/* Mode selector — compact horizontal Netflix cards */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
                {IDEA_MODES.map(m => {
                    const locked = m.proOnly && !isPro;
                    const isSelected = mode === m.value;
                    const color = MODE_COLORS[m.value] || '#6366f1';
                    const emoji = MODE_EMOJI[m.value] || '●';
                    return (
                        <Tooltip key={m.value} title={locked ? 'Pro plan required' : m.description} placement="top">
                            <Box
                                role="button"
                                tabIndex={locked ? -1 : 0}
                                aria-pressed={isSelected}
                                onClick={() => !locked && onModeChange(m.value)}
                                onKeyDown={(e) => !locked && (e.key === 'Enter' || e.key === ' ') && onModeChange(m.value)}
                                sx={{
                                    cursor: locked ? 'not-allowed' : 'pointer',
                                    opacity: locked ? 0.55 : 1,
                                    display: 'flex', flexDirection: 'row',
                                    alignItems: 'center', gap: 0.6,
                                    borderRadius: '8px',
                                    px: 1.25, py: 0.65,
                                    border: `1.5px solid ${isSelected ? color : alpha(isDark ? '#fff' : '#000', 0.1)}`,
                                    borderLeft: `3px solid ${isSelected ? color : alpha(color, 0.25)}`,
                                    background: isSelected
                                        ? `linear-gradient(135deg, ${alpha(color, isDark ? 0.22 : 0.12)} 0%, ${alpha(color, isDark ? 0.08 : 0.04)} 100%)`
                                        : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                    boxShadow: isSelected
                                        ? `0 0 0 1px ${alpha(color, 0.18)}, 0 3px 12px ${alpha(color, 0.2)}`
                                        : 'none',
                                    transform: isSelected ? 'translateY(-1px)' : 'none',
                                    transition: 'all 0.18s ease',
                                    '&:hover': !locked ? {
                                        transform: 'translateY(-1px)',
                                        border: `1.5px solid ${alpha(color, 0.45)}`,
                                        borderLeft: `3px solid ${color}`,
                                        boxShadow: `0 4px 14px ${alpha(color, isDark ? 0.25 : 0.15)}`,
                                        background: isSelected
                                            ? `linear-gradient(135deg, ${alpha(color, isDark ? 0.28 : 0.15)} 0%, ${alpha(color, isDark ? 0.1 : 0.06)} 100%)`
                                            : isDark ? alpha(color, 0.07) : alpha(color, 0.04),
                                    } : {},
                                }}
                            >
                                {locked
                                    ? <LockIcon sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }} />
                                    : <Typography sx={{ fontSize: '0.9rem', lineHeight: 1, userSelect: 'none', flexShrink: 0 }}>{emoji}</Typography>
                                }
                                <Typography sx={{
                                    fontSize: '0.76rem',
                                    fontWeight: isSelected ? 700 : 500,
                                    color: isSelected
                                        ? isDark ? alpha(color, 0.95) : color
                                        : locked ? 'text.disabled' : 'text.secondary',
                                    userSelect: 'none', whiteSpace: 'nowrap',
                                    transition: 'color 0.18s',
                                }}>
                                    {m.label}
                                </Typography>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>

            {/* Topic field — hidden for Double Down / Trending+You (pro only) */}
            {!hideTopicField && (
                <>
                    <TextField
                        fullWidth
                        placeholder={
                            mode === 'fill_gap'
                                ? 'Optional: add a niche or theme focus…'
                                : 'e.g. healthy recipes, tech gadgets, morning routines…'
                        }
                        value={topic}
                        onChange={onTopicChange}
                        disabled={loading}
                        variant="outlined"
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2.5, fontSize: '0.92rem',
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6366f1' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#6366f1',
                                    boxShadow: `0 0 0 3px ${alpha('#6366f1', 0.1)}`,
                                },
                            },
                        }}
                    />

                    {/* Example topics — compact Netflix cards */}
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, display: 'block' }}>
                        Popular topics:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
                        {EXAMPLE_TOPICS.map(t => {
                            const active = topic === t;
                            return (
                                <Box
                                    key={t}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => onTopicChange({ target: { value: t } })}
                                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onTopicChange({ target: { value: t } })}
                                    sx={{
                                        cursor: 'pointer',
                                        borderRadius: '6px',
                                        px: 1.25, py: 0.5,
                                        border: `1.5px solid ${active ? alpha('#6366f1', 0.5) : alpha('#6366f1', 0.15)}`,
                                        borderLeft: `4px solid ${active ? '#6366f1' : alpha('#6366f1', 0.25)}`,
                                        background: active
                                            ? isDark
                                                ? alpha('#6366f1', 0.18)
                                                : alpha('#6366f1', 0.08)
                                            : 'transparent',
                                        transition: 'all 0.15s ease',
                                        boxShadow: active ? `0 2px 10px ${alpha('#6366f1', 0.18)}` : 'none',
                                        '&:hover': {
                                            background: isDark ? alpha('#6366f1', 0.14) : alpha('#6366f1', 0.07),
                                            borderColor: alpha('#6366f1', 0.4),
                                            borderLeftColor: '#6366f1',
                                            transform: 'translateY(-1px)',
                                        },
                                    }}
                                >
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.76rem',
                                        fontWeight: active ? 700 : 500,
                                        color: active ? '#6366f1' : 'text.secondary',
                                        userSelect: 'none',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {t}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}

            {/* Platform filter — Netflix cards */}
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, display: 'block' }}>
                Filter by platform:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }}>
                {PLATFORMS.map(p => {
                    const isSelected = platform === p.value;
                    const color = p.value ? (PLATFORM_COLORS[p.value] || '#6366f1') : '#6366f1';
                    return (
                        <Box
                            key={String(p.value)}
                            role="button"
                            tabIndex={0}
                            onClick={() => onPlatformChange(p.value)}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onPlatformChange(p.value)}
                            sx={{
                                cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: 0.5,
                                borderRadius: '6px',
                                px: 1.25, py: 0.6,
                                border: `1.5px solid ${isSelected ? alpha(color, 0.55) : alpha(theme.palette.divider, 0.5)}`,
                                borderLeft: `4px solid ${isSelected ? color : alpha(color, 0.3)}`,
                                background: isSelected
                                    ? isDark
                                        ? `linear-gradient(135deg, ${alpha(color, 0.2)} 0%, ${alpha(color, 0.07)} 100%)`
                                        : `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha(color, 0.04)} 100%)`
                                    : 'transparent',
                                transition: 'all 0.2s ease',
                                boxShadow: isSelected ? `0 2px 10px ${alpha(color, 0.2)}` : 'none',
                                '&:hover': {
                                    background: isDark ? alpha(color, 0.1) : alpha(color, 0.06),
                                    borderColor: alpha(color, 0.45),
                                    borderLeftColor: color,
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 14px ${alpha(color, 0.18)}`,
                                },
                            }}
                        >
                            <Typography sx={{ fontSize: '0.8rem', lineHeight: 1 }}>{p.icon}</Typography>
                            <Typography variant="caption" sx={{
                                fontSize: '0.76rem',
                                fontWeight: isSelected ? 700 : 500,
                                color: isSelected ? color : 'text.secondary',
                                userSelect: 'none',
                                whiteSpace: 'nowrap',
                                transition: 'color 0.2s',
                            }}>
                                {p.label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Submit */}
            <Button
                fullWidth variant="contained"
                onClick={onSubmit}
                disabled={!isValid}
                size="large"
                startIcon={
                    loading
                        ? <CircularProgress size={18} sx={{ color: 'white' }} />
                        : <AutoAwesomeIcon />
                }
                sx={{
                    borderRadius: 2.5, py: 1.4, fontWeight: 700, fontSize: '0.95rem',
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    boxShadow: '0 4px 20px rgba(99,102,241,0.35)',
                    '&:hover': {
                        background: 'linear-gradient(135deg, #5855eb, #7c3aed)',
                        boxShadow: '0 6px 24px rgba(99,102,241,0.45)',
                        transform: 'translateY(-1px)',
                    },
                    '&:disabled': { background: 'rgba(156,163,175,0.35)', color: 'rgba(255,255,255,0.65)' },
                    transition: 'all 0.2s ease',
                }}
            >
                {loading ? 'Generating…' : 'Generate Ideas'}
            </Button>
        </Paper>
    );
}
